import { IProductData, TPathParams } from '@/utils';
import { TCartItem } from '@/lib/cart/useCart';
import { IS_PREVIEW } from '@/utils/env';
import {
  pushToElevarDataLayer,
  getUserProperties,
  transformProductToElevar,
  transformCartItemToElevar,
  getReferrerList,
} from './utils';
import { ElevarAddToCartEvent } from './types';

/**
 * Elevar add to cart tracking.
 * Call this function when a product is added to cart.
 * Should be used alongside your existing handleAddToCartGoogleTag.
 */
export const handleElevarAddToCart = (
  product: IProductData | TCartItem,
  params?: TPathParams,
  isComplete?: boolean
) => {
  if (IS_PREVIEW) return;

  const userProperties = getUserProperties();
  const referrerList = getReferrerList();

  // Transform product based on type
  let elevarProduct;
  if ('quantity' in product && 'type' in product) {
    // This is a TCartItem
    elevarProduct = transformProductToElevar(
      product as IProductData,
      params,
      isComplete,
      1,
      referrerList
    );
  } else {
    // This is an IProductData
    elevarProduct = transformProductToElevar(
      product as IProductData,
      params,
      isComplete,
      1,
      referrerList
    );
  }

  const event: ElevarAddToCartEvent = {
    event: 'dl_add_to_cart',
    user_properties: userProperties,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: {
          list: referrerList,
        },
        products: [elevarProduct],
      },
    },
  };

  pushToElevarDataLayer(event);
};

/**
 * Hook version for multiple products add to cart
 */
export const handleElevarAddToCartMultiple = (
  products: (IProductData | TCartItem)[],
  params?: TPathParams,
  isComplete?: boolean
) => {
  if (IS_PREVIEW) return;

  const userProperties = getUserProperties();
  const referrerList = getReferrerList();

  const elevarProducts = products.map((product, index) => {
    if ('quantity' in product && 'type' in product) {
      return transformProductToElevar(product as IProductData, params, isComplete, 1, referrerList);
    } else {
      return transformProductToElevar(
        product as IProductData,
        params,
        isComplete,
        index + 1,
        referrerList
      );
    }
  });

  const event: ElevarAddToCartEvent = {
    event: 'dl_add_to_cart',
    user_properties: userProperties,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: {
          list: referrerList,
        },
        products: elevarProducts,
      },
    },
  };

  pushToElevarDataLayer(event);
};
